import type { ReactNode } from "react";
import type {
  BannedPairPartner,
  CardFace,
  FleshAndBloodCard,
  ForeignData,
  FusionWorldCard,
  GameCard,
  LorcanaCard,
  MagicCard,
  OnePieceCard,
  PokemonCard,
  RiftboundCard,
  Ruling,
  StarWarsUnlimitedCard,
  CommonPagedCard,
} from "modules/cards/model/card";
import {
  ManaFontManaSymbols,
  ReplacedSymbolsOracleText,
} from "modules/common/components/ManaFontSymbols";
import {
  containsHtmlTags,
  costPrintedToManaFontClasses,
  firstUpper,
} from "utils/presentation";
import { escapeNonTagAngleBrackets } from "modules/cards/presentation/cardRulesText";
import { PokemonCardInfo } from "modules/common/components/CardDetailsDialog/PokemonCardInfo";
import { RiftboundCardInfo } from "modules/common/components/CardDetailsDialog/RiftboundCardInfo";
import { OnePieceRulesText } from "modules/common/components/CardDetailsDialog/OnePieceRulesText";
import { ManaFontTypeSymbols } from "modules/common/components/ManaFontSymbols";
import {
  CharacterIcon,
  DonIcon,
  EventIcon,
  LeaderIcon,
  StageIcon,
} from "modules/common/components/Icons/OnePieceTypeIcons";
import { formatOnePieceRarity } from "modules/cards/utils/onepieceRarity";
import {
  GameCardDossier,
  type DossierBadgeTone,
  type DossierStat,
} from "modules/common/components/CardDetailsDialog/GameCardDossier";
import {
  Brain,
  Footprints,
  Heart,
  Shield,
  Sparkles,
  Swords,
  Zap,
} from "lucide-react";

const CARD_COLOR_BADGE_TONES = {
  red: "error",
  blue: "info",
  green: "success",
  yellow: "warning",
  black: "neutral",
  white: "base",
} satisfies Readonly<Record<string, DossierBadgeTone>>;

const STAR_WARS_ASPECT_BADGE_TONES = {
  heroism: "base",
  villainy: "neutral",
  vigilance: "info",
  command: "success",
  aggression: "error",
  cunning: "warning",
} satisfies Readonly<Record<string, DossierBadgeTone>>;

const LORCANA_INK_BADGE_TONES = {
  amber: "warning",
  amethyst: "secondary",
  emerald: "success",
  ruby: "error",
  sapphire: "info",
  steel: "base",
} satisfies Readonly<Record<string, DossierBadgeTone>>;

export interface CardPresentationAdapter {
  tableTypeColumnWidth: string;
  tableNameColumnWidth: string;
  renderEssentialInfo(card: GameCard): ReactNode;
  renderTableType(types: string[]): ReactNode;
  renderTableCost(
    card: CommonPagedCard,
    excludedTypes: readonly string[]
  ): ReactNode;
  renderTableRarity(rarity: string): ReactNode;
  supportsCardActions: boolean;
  faceName(card: GameCard): string;
  foreignData(card: GameCard): ForeignData[] | undefined;
  bannedPairs(card: GameCard): Record<string, BannedPairPartner[]> | undefined;
  flippedFace(card: GameCard, flipped: boolean): CardFace | undefined;
  inlineFaces(card: GameCard): CardFace[] | undefined;
  rulings(card: GameCard): Ruling[] | undefined;
}

const TEXT_TYPE_TABLE_LAYOUT = {
  tableTypeColumnWidth: "7rem",
  tableNameColumnWidth: "max(9rem, calc(40% - 7rem))",
} as const;

const ICON_TYPE_TABLE_LAYOUT = {
  tableTypeColumnWidth: "5%",
  tableNameColumnWidth: "35%",
} as const;

function renderTextType(types: string[]) {
  const label = types.join(", ");
  return (
    <span className="text-secondary block w-full min-w-0 truncate text-center text-sm">
      {label}
    </span>
  );
}

function renderManaType(types: string[]) {
  return (
    <ManaFontTypeSymbols type={types.join(",")} className="justify-center" />
  );
}

function renderOnePieceType(types: string[]) {
  if (types.length === 0) return null;
  const icons: Record<string, ReactNode> = {
    Leader: <LeaderIcon size="small" />,
    Character: <CharacterIcon size="small" />,
    Event: <EventIcon size="small" />,
    Stage: <StageIcon size="small" />,
    "DON!!": <DonIcon size="small" />,
  };
  return icons[types[0]] ?? null;
}

function renderManaCost(card: CommonPagedCard) {
  return (
    <div className="flex gap-1">
      <ManaFontManaSymbols
        style={{ fontSize: "0.70em", marginLeft: "1px" }}
        costPrinted={card.costPrinted}
        extractClassesFunc={costPrintedToManaFontClasses}
      />
    </div>
  );
}

function renderNumericCostWhenPresent(card: CommonPagedCard) {
  if (card.costPrinted) return <div>{card.costPrinted}</div>;
  return typeof card.costValue === "number" && card.costValue !== 0 ? (
    <div>{card.costValue}</div>
  ) : null;
}

function renderNumericCostWhenPrinted(card: CommonPagedCard) {
  if (!card.costPrinted) return null;
  return (
    <div>
      {typeof card.costValue === "number" ? card.costValue : card.costPrinted}
    </div>
  );
}

function renderNumericCostExceptTypes(
  card: CommonPagedCard,
  excludedTypes: readonly string[]
) {
  if (excludedTypes.includes(card.type)) return null;

  const printedCost = card.costPrinted?.trim();
  if (printedCost) return <div>{printedCost}</div>;
  return typeof card.costValue === "number" ? <div>{card.costValue}</div> : null;
}

function renderTitleRarity(rarity: string) {
  return <div className="text-secondary text-sm">{firstUpper(rarity)}</div>;
}

function renderOnePieceRarity(rarity: string) {
  const { name, abbr } = formatOnePieceRarity(rarity);
  return (
    <div className="text-secondary flex items-baseline gap-1.5 text-sm">
      <span>{name}</span>
      <span className="text-base-content/40 text-xs">{abbr}</span>
    </div>
  );
}

// Unknown/dynamic games may use One-Piece-style <Keyword> syntax that an HTML
// parser silently swallows, and {X}-brace tokens that are not MTG mana.
// Escape non-tag brackets; if real HTML remains, the parser decodes the
// escapes back — if none remains, plain text renders verbatim without it.
function renderGenericCardText(text: string): ReactNode {
  const escaped = escapeNonTagAngleBrackets(text);
  if (containsHtmlTags(escaped)) {
    return (
      <ReplacedSymbolsOracleText
        oracleText={escaped}
        className="text-base-content ml-px"
        replaceSymbols={false}
      />
    );
  }
  return (
    <p className="text-base-content ml-px whitespace-pre-line text-base">
      {text}
    </p>
  );
}

function GenericCardInfo({
  card,
  renderText,
}: {
  card: GameCard;
  renderText?: (text: string) => ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="mt-2 flex flex-row items-center justify-between gap-1 md:mt-0">
        <h1 className="text-primary text-xl">{card.name}</h1>
        {card.costPrinted && (
          <span className="text-secondary">{card.costPrinted}</span>
        )}
      </div>
      {card.type && <p className="text-secondary text-lg">{card.type}</p>}
      {card.text &&
        (renderText ? renderText(card.text) : renderGenericCardText(card.text))}
      {card.flavorText && (
        <p className="text-secondary whitespace-pre-line text-sm italic">
          {card.flavorText}
        </p>
      )}
    </div>
  );
}

function MagicCardInfo({ card }: { card: GameCard }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="mt-2 flex flex-row items-center justify-between gap-1 md:mt-0">
        <h1 className="text-primary text-xl">
          {card.name.includes("//") ? card.name.split("//").at(0) : card.name}
        </h1>
        <ManaFontManaSymbols
          className="ml-px"
          costPrinted={card.costPrinted}
          extractClassesFunc={costPrintedToManaFontClasses}
        />
      </div>
      <p className="text-secondary text-lg">{card.type}</p>
      <ReplacedSymbolsOracleText
        oracleText={card.text}
        className="text-base-content ml-px"
      />
      {card.flavorText && (
        <ReplacedSymbolsOracleText
          oracleText={card.flavorText}
          className="text-secondary text-sm italic"
        />
      )}
    </div>
  );
}

export const GENERIC_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...TEXT_TYPE_TABLE_LAYOUT,
  renderEssentialInfo: (card) => <GenericCardInfo card={card} />,
  renderTableType: renderTextType,
  renderTableCost: renderNumericCostWhenPresent,
  renderTableRarity: renderTitleRarity,
  supportsCardActions: false,
  faceName: () => "",
  foreignData: () => undefined,
  bannedPairs: () => undefined,
  flippedFace: () => undefined,
  inlineFaces: () => undefined,
  rulings: () => undefined,
};

export const MTG_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...ICON_TYPE_TABLE_LAYOUT,
  renderEssentialInfo: (card) => <MagicCardInfo card={card} />,
  renderTableType: renderManaType,
  renderTableCost: renderManaCost,
  renderTableRarity: renderTitleRarity,
  supportsCardActions: true,
  faceName: (card) => (card as MagicCard).features.faceName ?? "",
  foreignData: (card) => (card as MagicCard).features.foreignData,
  bannedPairs: () => undefined,
  flippedFace: (card, flipped) =>
    flipped ? (card as MagicCard).features.otherFaces?.[0] : undefined,
  inlineFaces: (card) => {
    const features = (card as MagicCard).features;
    if (
      features.layout === "transform" ||
      features.layout === "modal_dfc" ||
      features.layout === "meld" ||
      features.layout === "reversible_card" ||
      features.layout === "art_series"
    ) {
      return undefined;
    }
    return features.otherFaces;
  },
  rulings: (card) => (card as MagicCard).features.rulings,
};

export const POKEMON_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...GENERIC_CARD_PRESENTATION_ADAPTER,
  renderEssentialInfo: (card) => <PokemonCardInfo card={card as PokemonCard} />,
  supportsCardActions: true,
};

export const RIFTBOUND_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...GENERIC_CARD_PRESENTATION_ADAPTER,
  ...ICON_TYPE_TABLE_LAYOUT,
  renderEssentialInfo: (card) => (
    <RiftboundCardInfo card={card as RiftboundCard} />
  ),
  renderTableType: renderManaType,
  renderTableCost: renderNumericCostExceptTypes,
  supportsCardActions: true,
};

export const ONE_PIECE_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...GENERIC_CARD_PRESENTATION_ADAPTER,
  ...ICON_TYPE_TABLE_LAYOUT,
  renderEssentialInfo: (card) => (
    <GenericCardInfo
      card={card}
      renderText={(text) => <OnePieceRulesText text={text} className="ml-px" />}
    />
  ),
  renderTableType: renderOnePieceType,
  renderTableCost: renderNumericCostWhenPrinted,
  renderTableRarity: renderOnePieceRarity,
  bannedPairs: (card) => (card as OnePieceCard).features.bannedPairs,
};

export const STAR_WARS_UNLIMITED_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter =
  {
    ...GENERIC_CARD_PRESENTATION_ADAPTER,
    renderEssentialInfo: (card) => {
      const swuCard = card as StarWarsUnlimitedCard;
      const features = swuCard.features;
      const stats: DossierStat[] = [];
      if (features.swuPower > 0) {
        stats.push({
          label: "Power",
          value: features.swuPower,
          icon: Swords,
          tone: "error",
        });
      }
      if (features.swuHP > 0) {
        stats.push({
          label: "HP",
          value: features.swuHP,
          icon: Shield,
          tone: "info",
        });
      }

      return (
        <GameCardDossier
          card={swuCard}
          costTone="warning"
          badges={[
            {
              label: "Aspects",
              values: features.swuAspects ?? [],
              toneByValue: STAR_WARS_ASPECT_BADGE_TONES,
            },
            {
              label: "Arena",
              values: features.swuArena ? [features.swuArena] : [],
              tone: "accent",
            },
            {
              label: "Traits",
              values: features.swuTraits ?? [],
              tone: "secondary",
            },
          ]}
          stats={stats}
          callouts={
            features.swuEpicAction
              ? [{ label: "Epic action", text: features.swuEpicAction }]
              : []
          }
        />
      );
    },
    supportsCardActions: true,
  };

export const LORCANA_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...GENERIC_CARD_PRESENTATION_ADAPTER,
  renderEssentialInfo: (card) => {
    const lorcanaCard = card as LorcanaCard;
    const features = lorcanaCard.features;
    const type = lorcanaCard.type.toLowerCase();
    const hasCharacterStats = type.includes("character");
    const hasLocationStats = type.includes("location");
    const stats: DossierStat[] = [];

    if (hasCharacterStats || features.lorcanaStrength > 0) {
      stats.push({
        label: "Strength",
        value: features.lorcanaStrength,
        icon: Swords,
      });
    }
    if (
      hasCharacterStats ||
      hasLocationStats ||
      features.lorcanaWillpower > 0
    ) {
      stats.push({
        label: "Willpower",
        value: features.lorcanaWillpower,
        icon: Shield,
      });
    }
    if (hasCharacterStats || hasLocationStats || features.lorcanaLore > 0) {
      stats.push({
        label: "Lore",
        value: features.lorcanaLore,
        icon: Sparkles,
      });
    }
    if (hasLocationStats || features.lorcanaMoveCost > 0) {
      stats.push({
        label: "Move",
        value: features.lorcanaMoveCost,
        icon: Footprints,
      });
    }

    return (
      <GameCardDossier
        card={lorcanaCard}
        badges={[
          {
            label: "Inks",
            values: features.lorcanaInks ?? [],
            toneByValue: LORCANA_INK_BADGE_TONES,
          },
          {
            label: "Classification",
            values: features.lorcanaClassifications ?? [],
            tone: "secondary",
          },
          {
            label: "Keywords",
            values: features.lorcanaKeywords ?? [],
          },
          {
            label: "Ink",
            values: features.lorcanaInkable ? ["Inkable"] : [],
            tone: "accent",
          },
        ]}
        stats={stats}
      />
    );
  },
  supportsCardActions: true,
};

export const FUSION_WORLD_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter = {
  ...GENERIC_CARD_PRESENTATION_ADAPTER,
  renderEssentialInfo: (card) => {
    const fusionCard = card as FusionWorldCard;
    const features = fusionCard.features;

    return (
      <GameCardDossier
        card={fusionCard}
        badges={[
          {
            label: "Colors",
            values: features.fusionWorldColors ?? [],
            toneByValue: CARD_COLOR_BADGE_TONES,
          },
          {
            label: "Traits",
            values: features.fusionWorldTraits ?? [],
            tone: "secondary",
          },
        ]}
        stats={[
          {
            label: "Power",
            value: features.fusionWorldPower,
            icon: Swords,
          },
          {
            label: "Combo",
            value: features.fusionWorldComboPower,
            icon: Zap,
          },
        ]}
      />
    );
  },
  supportsCardActions: true,
};

export const FLESH_AND_BLOOD_CARD_PRESENTATION_ADAPTER: CardPresentationAdapter =
  {
    ...GENERIC_CARD_PRESENTATION_ADAPTER,
    renderEssentialInfo: (card) => {
      const fabCard = card as FleshAndBloodCard;
      const features = fabCard.features;
      const stats: DossierStat[] = [];

      if (features.fabPitch > 0) {
        stats.push({
          label: "Pitch",
          value: features.fabPitch,
          icon: Zap,
          // Pitch 1/2/3 cards are red/yellow/blue in Flesh and Blood
          tone: (["error", "warning", "info"] as const)[features.fabPitch - 1],
        });
      }
      if (features.fabPower) {
        stats.push({
          label: "Power",
          value: features.fabPower,
          icon: Swords,
          tone: "warning",
        });
      }
      if (features.fabDefense) {
        stats.push({
          label: "Defense",
          value: features.fabDefense,
          icon: Shield,
          tone: "neutral",
        });
      }
      if (features.fabHealth) {
        stats.push({ label: "Health", value: features.fabHealth, icon: Heart });
      }
      if (features.fabIntelligence) {
        stats.push({
          label: "Intellect",
          value: features.fabIntelligence,
          icon: Brain,
        });
      }
      if (features.fabArcane) {
        stats.push({
          label: "Arcane",
          value: features.fabArcane,
          icon: Sparkles,
        });
      }

      return (
        <GameCardDossier
          card={fabCard}
          badges={[
            {
              label: "Colors",
              values: features.fabColors ?? [],
              toneByValue: CARD_COLOR_BADGE_TONES,
            },
            {
              label: "Traits",
              values: features.fabTraits ?? [],
              tone: "secondary",
            },
            {
              label: "Keywords",
              values: features.fabKeywords ?? [],
            },
          ]}
          stats={stats}
        />
      );
    },
    supportsCardActions: true,
  };
